import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { REACT_QUERY_KEYS } from '@hungryroot/rest';
import {
  getFilterPairings,
  type SwappableParamsResponse,
} from '@hungryroot/rest/api/filter_pairings/get';

export function useSwappableRecipesQuery(
  data: {
    orderId: number;
    recipeId: number;
  },
  options: { enabled: boolean } = { enabled: true }
) {
  const { enabled } = options;
  const { orderId, recipeId } = data;

  return useQuery({
    queryKey: [
      REACT_QUERY_KEYS.PAIRINGS,
      'filter_pairings',
      'swappable_recipes',
      orderId,
      recipeId,
    ],
    queryFn: async () => {
      const res = await getFilterPairings(
        new URLSearchParams({
          order_id: orderId.toString(),
          swap_ingredients_recipe: recipeId.toString(),
        })
      );

      if (res.status !== 200) {
        throw res.data;
      }

      return res.data as SwappableParamsResponse;
    },
    enabled,
  });
}

type UseSwappableRecipiesResult =
  | {
      eligible: false;
    }
  | {
      eligible: true;
      query: UseQueryResult<SwappableParamsResponse>;
    };

export function useSwappableRecipes(data: {
  orderId: number;
  recipeId: number | null;
  servingSize: number;
}): UseSwappableRecipiesResult {
  const { orderId, recipeId, servingSize } = data;
  const query = useSwappableRecipesQuery(
    {
      orderId,
      recipeId: recipeId === null ? 0 : recipeId,
    },
    {
      enabled: servingSize === 2 && recipeId !== null,
    }
  );

  if (servingSize === 4 || recipeId === null) {
    return {
      eligible: false,
    };
  }

  return {
    eligible: true,
    query,
  };
}
